<template>
<div class="p-0 sm:p-8 sm:flex sm:items-center sm:justify-center min-h-screen bg-gradient-to-br from-sky-100 via-blue-200 to-blue-500">

    <div
      class="w-full sm:w-[800px] sm:h-[650px] border-0 sm:rounded-2xl sm:shadow-2xl sm:flex overflow-hidden bg-background-white"
      :class="{ 'items-center justify-center': showConfirmation || globalError || loadingState}"
    >
      <!-- Left Sidebar (Desktop only) -->
      <SideBar
        v-if="!showConfirmation && !globalError && !loadingState"
        :current-step="currentStep"
        :therapist-name="therapistName"
        :selected-meeting-type="selectedMeetingType"
        :selected-date-time="selectedDateTime"
        :format-date="formatDate"
        @back="goBack"
      />

      <!-- Vertical Divider (Desktop only) -->
      <div class="hidden sm:block w-px bg-gray-100"></div>

      <!-- Main Container -->
      <div class="flex-1 w-full flex flex-col">

        <!-- Mobile Top Bar -->
       <MobileTopBar
          v-if="!showConfirmation && !globalError && !loadingState"
          :current-step="currentStep"
          :therapist-name="therapistName"
          :selected-meeting-type="selectedMeetingType"
          :selected-date-time="selectedDateTime"
          :format-date="formatDate"
          @back="goBack"
        />

        <!-- Vertical Divider (Mobile only) -->
        <div class="sm:hidden h-px bg-gray-200"></div>

        <!-- Main Content -->
        <div class="p-5 sm:p-7 flex-1 min-h-0">
          <Loading v-if="loadingState" :state="loadingState"  />

          <div v-else>
            <Confirmation v-if="showConfirmation" @restart="restart" />
            <Error
              v-if="globalError"
              :title="globalError.title"
              :message="globalError.message"
              @restart="restart"
            />
            <div v-else class="h-full">
              <div v-if="currentStep === 1" class="h-full">
                <MeetingType
                  :meetingTypes="meetingTypes"
                  :currentStep="1"
                  :totalSteps="totalSteps"
                  @next="handleMeetingType"
                  @back="goBack"
                />
              </div>
              <div v-else-if="currentStep === 2" class="h-full">
                <DateTime
                  :meetingType="selectedMeetingType"
                  :availabilities="availabilities"
                  :current-step="2"
                  :total-steps="totalSteps"
                  @next="handleDateTime"
                  @back="goBack"
                />
              </div>
              <div v-else-if="currentStep === 3" class="h-full">
                <BookingForm
                  @submit="handleBookingForm"
                  @back="goBack"
                  :meetingType="selectedMeetingType"
                  :dateTime="selectedDateTime"
                  :current-step="3"
                  :total-steps="totalSteps"
                  :therapist-country="therapistCountry"
                />
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch } from 'vue'
import MeetingType from './components/meeting_type/MeetingType.vue'
import DateTime from './components/date_and_time/DateTime.vue'
import BookingForm from './components/booking/Form.vue'
import { dataService } from './services/data'
import Confirmation from './components/Confirmation.vue'
import SideBar from './components/SideBar.vue'
import MobileTopBar from './components/MobileTopBar.vue'
import Loading from './components/Loading.vue'
import Error from './components/Error.vue'
import { mixpanelService } from './services/mixpanel'
import { APP_VERSION, APP_NAME } from './version'


const currentStep = ref(1)
const meetingTypes = ref([])
const availabilities = ref([])
const selectedMeetingType = ref(null)
const selectedDateTime = ref(null)
const showConfirmation = ref(false)
const globalError = ref(null)
const filledForm = ref(null)
const loadingState = ref(null)
const therapistName = ref(null)
const dataCollectionPermission = ref(0)
const therapistCountry = ref(null)
const isIframe = ref(false)
let therapistId = '' 
const totalSteps = 3
const lastStep = ref(currentStep.value)

onMounted(async () => {
  loadingState.value = 'initial'
  try {
    const urlParams = new URLSearchParams(window.location.search)
    
    // Check if version info is requested
    if (urlParams.get('version') !== null) {
      document.body.innerHTML = `<pre style="font-family: monospace; padding: 20px;">${JSON.stringify({
        name: APP_NAME,
        version: APP_VERSION,
        environment: import.meta.env.MODE,
        timestamp: new Date().toISOString()
      }, null, 2)}</pre>`
      return
    }
    
    therapistId = urlParams.get('id')

    if (!therapistId) {
      throw new Error('Missing therapistId in URL')
    }

    // Check if running in iframe
    try {
      isIframe.value = window.self !== window.top
    } catch (e) {
      isIframe.value = null
    }

    // Load therapist info
    const therapist = await dataService.getTherapist(therapistId)
    therapistName.value = therapist
    
    // Get data collection permission
    dataCollectionPermission.value = dataService.getDataCollectionPermission() || 0
    therapistCountry.value = dataService.getTherapistCountry()

    // Load meeting types
    meetingTypes.value = await dataService.getMeetingTypes(therapistId)

    // Check if there are any meeting types available
    if (!meetingTypes.value || meetingTypes.value.length === 0) {
      globalError.value = {
        title: 'Keine Termine verfügbar',
        message: 'Derzeit sind leider keine freien Termine verfügbar.<br>Bitte versuche es später erneut.'
      }
      loadingState.value = null
      return
    }

    // Check and apply meeting type from URL if available
    const meetingTypeParam = urlParams.get('type') 

    if (meetingTypeParam && meetingTypes.value.length > 0) {
      const matchedType = meetingTypes.value.find(t => t.id === meetingTypeParam || t.slug === meetingTypeParam)

      if (matchedType) {
        try {
          const availabilitiesData = await dataService.getDatesAndTimes(matchedType.id)

          if (
              availabilitiesData &&
              Object.keys(availabilitiesData).length > 0 &&
              Object.values(availabilitiesData).some(slots => Array.isArray(slots) && slots.length > 0)
            ){
            selectedMeetingType.value = matchedType
            availabilities.value = availabilitiesData
            currentStep.value = 2
            history.replaceState({ step: 2 }, '')
            lastStep.value = 2
          }
        } catch (err) {
          console.error('Error loading error loading specific meeting type', err)
        }
      }
    }
  } catch (err) {
    console.error('Error loading data:', err)
    globalError.value = getApiError(err) ?? {
      title: 'Keine Daten verfügbar',
      message: 'Über diesen Link sind derzeit keine Buchungen möglich.<br>Bitte überprüfe den Link oder versuche es später erneut.'
    }
  } finally {
    loadingState.value = null
  }

  
  
  if (!history.state) {
    history.replaceState({ step: currentStep.value }, '')
  }
    window.addEventListener('popstate', handlePopState)
})

function getApiError(err) {
  if (err.code === 'ERR_NETWORK' || !err.response) {
    return {
      title: 'Verbindungsfehler',
      message: 'Der Server ist derzeit nicht erreichbar.<br>Bitte überprüfe deine Internetverbindung und versuche es später erneut.'
    }
  }
  if (err.response?.status >= 500) {
    return {
      title: 'Serverfehler',
      message: `Der Server ist momentan nicht verfügbar (Fehler ${err.response.status}).<br>Bitte versuche es später erneut.`
    }
  }
  return null
}

function formatDate(dateTimeStr) {
  const options = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }
  return new Date(dateTimeStr).toLocaleString('de-DE', options)
}

async function handleMeetingType(type) {
  try {
    const availabilitiesData = await dataService.getDatesAndTimes(type.id)

    if (type && availabilitiesData) {
      selectedMeetingType.value = type
      availabilities.value = availabilitiesData
      
      currentStep.value = 2
      history.pushState({ step: 2 }, '')
      lastStep.value = 2
    } else {
      globalError.value = {
        title: 'Fehler beim Laden',
        message: 'Verfügbarkeiten konnten nicht geladen werden. Bitte versuche es später erneut.'
      }
    }
  } catch (err) {
    console.error('Error loading availabilities:', err)
    globalError.value = getApiError(err) ?? {
      title: 'Fehler beim Laden',
      message: 'Verfügbarkeiten konnten nicht geladen werden. Bitte versuche es später erneut.'
    }
  }
}

function goBack() {
  if (currentStep.value === 3) {
    selectedDateTime.value = null
    filledForm.value = null
  } else if (currentStep.value === 2) {
    selectedMeetingType.value = null
    availabilities.value = []
  }

  if (currentStep.value > 1) {
    currentStep.value -= 1
  }
}

function handleDateTime(dateTime) {
  selectedDateTime.value = dateTime
  
  // Track date/time selection
  mixpanelService.track('Client Booking Started', {
    isIframe: isIframe.value,
  }, dataCollectionPermission.value, therapistId)
  
  currentStep.value = 3
  history.pushState({ step: 3 }, '')
  lastStep.value = 3
}

function restart() {
  // Track restart action
  mixpanelService.track('Client Booking Restarted', {
    isIframe: isIframe.value,
  }, dataCollectionPermission.value, therapistId)
  
  showConfirmation.value = false
  currentStep.value = 1
  selectedMeetingType.value = null
  selectedDateTime.value = null
  filledForm.value = null
  globalError.value = null
  dataService.clearCache()

  // Reload meeting types
  loadingState.value = 'initial'
  dataService.getMeetingTypes(therapistId)
    .then(data => {
      meetingTypes.value = data
      if (!data || data.length === 0) {
        globalError.value = {
          title: 'Keine Termine verfügbar',
          message: 'Derzeit sind leider keine freien Termine verfügbar.<br>Bitte versuche es später erneut.'
        }
      }
    })
    .catch(err => {
      console.error('Error reloading meeting types:', err)
      globalError.value = getApiError(err) ?? {
        title: 'Keine Daten verfügbar',
        message: 'Über diesen Link sind derzeit keine Buchungen möglich.<br>Bitte überprüfe den Link oder versuche es später erneut.'
      }
    })
    .finally(() => loadingState.value = null)
}

async function handleBookingForm(formData) {
  try {
    loadingState.value = 'formSubmit'

    // Create booking payload with separate clientData and customData
    const bookingPayload = {
      startTime: selectedDateTime.value,
      uid: therapistId,
      meetingTypeId: selectedMeetingType.value.id,
      clientData: formData.clientData,
      notes: formData.notes
    }

    // Add customData only if it exists
    if (formData.customData) {
      bookingPayload.customData = formData.customData
    }
    
    const statusCode = await dataService.createBooking(bookingPayload)
    
    if(statusCode !== 201) {
      throw new Error('Booking failed. Status code: ' + statusCode)
    }

    // Track successful booking
    mixpanelService.track('Client Booking Completed', {
      hasNotes: !!formData.notes,
      isIframe: isIframe.value,
    }, dataCollectionPermission.value, therapistId)

    // Clear state and show confirmation
    filledForm.value = formData
    currentStep.value = 0
    loadingState.value = null
    showConfirmation.value = true
    dataService.clearCache()

  } catch (err) {
    console.error('Booking failed:', err)
    loadingState.value = null
    globalError.value = getApiError(err) ?? {
      title: 'Buchung fehlgeschlagen',
      message: 'Es ist ein Fehler bei der Buchung aufgetreten. Bitte versuche es später erneut.'
    }
  }
}

function handlePopState(event) {
  const newStep = event.state?.step ?? 1

  if (newStep < currentStep.value && currentStep.value > 1) {
    console.log('Back detected')
    goBack()
  }

  lastStep.value = newStep
}

onUnmounted(() => {
  window.removeEventListener('popstate', handlePopState)
})
</script>
